import { IonButton, useIonAlert, useIonToast } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { useMutation } from '@apollo/client/react';
import { FOLLOW_CHANNEL, UNFOLLOW_CHANNEL } from '../../../../graphql/mutations/followChannel';
import { GET_CHANNEL_TIMELINE } from '../../../../graphql/queries/channelTimeline';
import type {
  FollowChannelMutation,
  FollowChannelMutationVariables,
  UnfollowChannelMutation,
  UnfollowChannelMutationVariables,
} from '../../../../types/graphql-generated';

interface FollowChannelButtonProps {
  channelId: string;
  viewerIsFollowing: boolean;
}

// Follow / unfollow action for a channel the viewer does not own. Both actions
// ask for confirmation first. Returning the channel from the mutation lets
// Apollo refresh the normalized cache (label + followers count); the timeline is
// refetched explicitly because it is a list keyed by the current follow set.
export const FollowChannelButton = ({ channelId, viewerIsFollowing }: FollowChannelButtonProps) => {
  const [presentAlert] = useIonAlert();
  const [presentToast] = useIonToast();

  const [followChannel, { loading: following }] = useMutation<
    FollowChannelMutation,
    FollowChannelMutationVariables
  >(FOLLOW_CHANNEL, {
    refetchQueries: [GET_CHANNEL_TIMELINE],
  });
  const [unfollowChannel, { loading: unfollowing }] = useMutation<
    UnfollowChannelMutation,
    UnfollowChannelMutationVariables
  >(UNFOLLOW_CHANNEL, {
    refetchQueries: [GET_CHANNEL_TIMELINE],
  });

  const loading = following || unfollowing;
  const label = viewerIsFollowing ? 'Dejar de seguir' : 'Seguir';

  const showError = (message: string) => {
    presentToast({
      message,
      duration: 3000,
      position: 'bottom',
      positionAnchor: 'app-footer',
      color: 'danger',
      buttons: [{ icon: closeOutline, role: 'cancel' }],
    });
  };

  const toggleFollow = async () => {
    const variables = { input: { channelId } };
    try {
      if (viewerIsFollowing) {
        const { data } = await unfollowChannel({ variables });
        return data?.unfollowChannel?.errors ?? [];
      }
      const { data } = await followChannel({ variables });
      return data?.followChannel?.errors ?? [];
    } catch (error) {
      console.error('Error toggling channel follow:', error);
      return ['No se ha podido completar la acción. Inténtalo de nuevo más tarde.'];
    }
  };

  const runToggle = async () => {
    const errors = await toggleFollow();
    if (errors.length > 0) {
      showError(errors[0]);
    }
  };

  const handleClick = () => {
    if (viewerIsFollowing) {
      presentAlert({
        header: 'Dejar de seguir',
        message: '¿Quieres dejar de seguir este canal?',
        buttons: [
          { text: 'Cancelar', role: 'cancel' },
          { text: 'Dejar de seguir', role: 'destructive', handler: () => runToggle() },
        ],
      });
    } else {
      presentAlert({
        header: 'Seguir canal',
        message: '¿Quieres seguir este canal?',
        buttons: [
          { text: 'Cancelar', role: 'cancel' },
          { text: 'Seguir', handler: () => runToggle() },
        ],
      });
    }
  };

  return (
    <IonButton
      size="small"
      fill={viewerIsFollowing ? 'outline' : 'solid'}
      onClick={handleClick}
      disabled={loading}
      data-testid="follow-channel-button"
    >
      {label}
    </IonButton>
  );
};
